import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "cookie_consent";
const DECLINE_RESHOW_DAYS = 30;

function shouldShowBanner(): boolean {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return true;
  if (stored === "accepted") return false;

  try {
    const parsed = JSON.parse(stored);
    if (parsed.status === "declined") {
      const daysSince = (Date.now() - parsed.timestamp) / (1000 * 60 * 60 * 24);
      return daysSince >= DECLINE_RESHOW_DAYS;
    }
  } catch {
    return true;
  }
  return true;
}

export function CookieBanner() {
  const [visible, setVisible] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "accepted") return false;
    return false;
  });

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "accepted") return;

    const timer = setTimeout(() => {
      setVisible(shouldShowBanner());
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ status: "declined", timestamp: Date.now() })
    );
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-in slide-in-from-bottom-4 duration-500">
      <div className="bg-card border-t border-border shadow-lg">
        <div className="max-w-5xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Cookie className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              Utilizamos cookies para melhorar sua experiência. Ao continuar navegando, você concorda com nossa{" "}
              <Link
                to="/politica-de-privacidade"
                className="text-primary hover:underline font-medium"
              >
                Política de Privacidade
              </Link>
              .
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={handleDecline}>
              Recusar
            </Button>
            <Button size="sm" onClick={handleAccept}>
              Aceitar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
